import { useEffect, useState } from 'react';
import { Alert, Button, Stack } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router';
import { ADMIN_JIT_ACTIVATIONS_QUERY_KEY, ADMIN_JIT_MY_ACCESS_QUERY_KEY, adminJitApi } from '../../../api/adminConsole';
import { ADMIN_CONSOLE_ME_QUERY_KEY } from '../../../lib/adminSession';
import { activationBannerItems } from '../../../lib/jitAccess';

/**
 * "Super Admin active — 1h 12m left · End now" across the top of every console page while the
 * viewer holds a just-in-time activation (RBAC Phase 4). Hidden when JIT is off, nothing is
 * active, or `/admin/iam/me/access` fails (the banner never blocks the page).
 */
export function JitActivationBanner({ enabled }: { enabled: boolean }) {
  const qc = useQueryClient();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);
  const accessQ = useQuery({
    queryKey: ADMIN_JIT_MY_ACCESS_QUERY_KEY,
    queryFn: () => adminJitApi.myAccess(),
    enabled,
    refetchInterval: 60_000,
    retry: false,
  });
  const end = useMutation({
    mutationFn: (id: string) => adminJitApi.end(id),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ADMIN_JIT_MY_ACCESS_QUERY_KEY }),
        qc.invalidateQueries({ queryKey: ADMIN_JIT_ACTIVATIONS_QUERY_KEY }),
        qc.invalidateQueries({ queryKey: ADMIN_CONSOLE_ME_QUERY_KEY }),
      ]);
    },
  });
  const items = enabled && accessQ.data?.mode !== 'off' ? activationBannerItems(accessQ.data, now) : [];
  if (items.length === 0) return null;
  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      {items.map((i) => (
        <Alert
          key={i.activationId}
          severity="warning"
          variant="outlined"
          action={
            <Stack direction="row" gap={1}>
              <Button size="small" color="inherit" component={RouterLink} to="/my-access">
                My access
              </Button>
              <Button size="small" color="inherit" disabled={end.isPending} onClick={() => end.mutate(i.activationId)}>
                End now
              </Button>
            </Stack>
          }
        >
          <strong>{i.roleName}</strong> is active — {i.remaining} left.
          {i.reportOnly ? ' (You also hold it standing, so this changed nothing.)' : ''}
        </Alert>
      ))}
    </Stack>
  );
}
